import type { SelectedFilterOption } from 'features/charts-v2/types'
import {
  NOT_PROMOTIONAL,
  PROMOTION_PREFIX,
} from 'features/charts-v2/utils/to-yogiql-chart-request'
import type { FilterOption, FilterOptionValue } from 'features/filters/types'
import { FilterTypes } from 'features/filters/types'
import { isNullValueAlias } from 'src/constants/filter-values'

/** One filter's selected values that the project's filter options no longer
 *  offer — typically a brand/product whose data was removed. */
export type StaleFilterValues = {
  field: string
  values: string[]
  /** The filter's operator: an `is not` on a missing value excludes nothing,
   *  so it can't empty a tile — the notice words it differently. */
  operator: string
  /** Every one of the filter's values is stale, so removing them removes the
   *  filter itself (on a tile, the field then follows the dashboard's). */
  emptiesFilter: boolean
}

/** Every form a stored selection can take for one offered value: the name,
 *  or (once a YogiQL read has resolved the name) its project/cluster uuid or
 *  any of its identifiers — the same ids `resolveNames` maps a name onto.
 *  Cached per option list: the lists are stable store objects, and the check
 *  runs on every tile render. */
const formsCache = new WeakMap<FilterOptionValue[], Set<string>>()
const knownForms = (offered: FilterOptionValue[]): Set<string> => {
  const cached = formsCache.get(offered)
  if (cached) return cached
  const forms = new Set<string>()
  for (const v of offered) {
    forms.add(v.value)
    for (const id of [v.proj_uuid, v.cluster_uuid, ...(v.identifiers ?? [])]) {
      if (id) forms.add(id)
    }
  }
  formsCache.set(offered, forms)
  return forms
}

/** The spellings the YogiQL read treats as one value — a match on any of them
 *  means the value still exists:
 *  - promotion types with and without the legacy `Promotional, ` prefix
 *    (PROD-6108: options and saved filters may disagree during the move);
 *  - a boolean custom field's `<field>` / `true` / `True` and
 *    `Not <field>` / `false` / `False` (`booleanFilter`'s spellings). */
const equivalentSpellings = (field: string, value: string): string[] => {
  if (field === 'promotion') {
    const bare = value.startsWith(PROMOTION_PREFIX)
      ? value.slice(PROMOTION_PREFIX.length)
      : value
    return [bare, `${PROMOTION_PREFIX}${bare}`]
  }
  const isTrue = [field, 'true', 'True']
  if (isTrue.includes(value)) return isTrue
  const isFalse = [`Not ${field}`, 'false', 'False']
  if (isFalse.includes(value)) return isFalse
  return [value]
}

/** Values that stand for null rows rather than an offered option. */
const namesNullRows = (field: string, value: string): boolean =>
  isNullValueAlias(value) ||
  (field === 'promotion' && value === NOT_PROMOTIONAL)

/**
 * The selected values the project no longer has, per filter. Matches what the
 * YogiQL read treats as "no longer in this hub" (`resolveNames`), but runs on
 * an already-read filter list, so it also catches legacy-form filters that the
 * read passes through untouched — the case where a stale value still reaches
 * the query and the tile comes back empty.
 *
 * Deliberately conservative — a false positive would prompt someone to delete
 * a working filter:
 * - only select fields whose option carries a real `values` list (a per-field
 *   options stub has none, which means "unknown", not "empty");
 * - a field missing from `filterOptions` is skipped, not reported;
 * - null-row labels ("No Value", "Not Promotional") are never stale;
 * - a value matches under any spelling the YogiQL read treats as the same;
 * - range / numeric selections carry no discrete values and are skipped.
 */
export const findStaleFilterValues = (
  filters: SelectedFilterOption[],
  filterOptions: FilterOption[],
): StaleFilterValues[] => {
  const stale: StaleFilterValues[] = []
  for (const filter of filters) {
    if (!filter.values.length) continue
    const option = filterOptions.find((o) => o.field === filter.field)
    if (
      option?.filter_type !== FilterTypes.SELECT ||
      !Array.isArray(option.values)
    ) {
      continue
    }
    const forms = knownForms(option.values)
    const missing = filter.values.filter(
      (value) =>
        !namesNullRows(filter.field, value) &&
        !equivalentSpellings(filter.field, value).some((v) => forms.has(v)),
    )
    if (missing.length) {
      stale.push({
        field: filter.field,
        values: missing,
        operator: filter.operator,
        emptiesFilter: missing.length === filter.values.length,
      })
    }
  }
  return stale
}

/**
 * `filters` with every stale value removed. A filter left with no values is
 * removed entirely — which on a tile means the field falls back to the
 * dashboard's global filter (child-wins is per field), so callers must say so
 * when they offer this.
 */
export const pruneStaleFilterValues = (
  filters: SelectedFilterOption[],
  stale: StaleFilterValues[],
): SelectedFilterOption[] => {
  if (!stale.length) return filters
  return filters.flatMap((filter) => {
    const drop = stale.find((s) => s.field === filter.field)
    if (!drop) return [filter]
    const values = filter.values.filter((v) => !drop.values.includes(v))
    return values.length ? [{ ...filter, values }] : []
  })
}
